import { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import {
    Card,
    Stack,
    Typography,
    Box,
    Button,
    TextField,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../api';

const cookies = new Cookies();
const authHeaders = () => ({ headers: { Authorization: cookies.get('TOKEN') } });

const REF_LABELS = {
    linkedStudents: 'linked student accounts',
    tutorAssignments: 'accounts assigned to this tutor',
    classesAsTutor: 'classes teaching',
    classesAsStudent: 'classes enrolled in',
    lessons: 'lessons',
    attendance: 'attendance records',
    progressReports: 'progress reports',
    invoices: 'invoices',
};

// Danger zone panel — hard-delete an account. Rendered only when the account
// is already archived. The flow deliberately has multiple gates:
//
//   1. Click "Permanently delete…" → opens the dialog (preview loads).
//   2. Dialog shows every cross-collection reference the server can see;
//      if any exist, the confirm button never becomes enabled.
//   3. Type the account's email exactly to unlock the confirm button.
//   4. Confirm → DELETE fires with { confirmEmail } body; server rechecks
//      everything and refuses if anything changed since the preview.
//
// After a successful delete we navigate back to the appropriate list page
// (parents/students/teachers) so the just-deleted row can't be re-opened.
const AccountDangerZone = ({ account }) => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [preview, setPreview] = useState(null);
    const [previewErr, setPreviewErr] = useState('');
    const [confirmEmail, setConfirmEmail] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) {
            setPreview(null);
            setPreviewErr('');
            setConfirmEmail('');
            setError('');
            return;
        }
        axios
            .get(`${API_URL}/api/admin/accounts/${account._id}/delete-preview`, authHeaders())
            .then((r) => setPreview(r.data))
            .catch((e) => setPreviewErr(e?.response?.data?.message || 'Failed to load references.'));
    }, [open, account._id]);

    if (account.accountStatus !== 'archived') return null;

    const emailMatches =
        confirmEmail.trim().toLowerCase() === String(account.email || '').toLowerCase();
    const hasReferences = preview?.references?.total > 0;
    const canConfirm = preview && !hasReferences && emailMatches && !busy;

    const listBackByRole = () => {
        if (account.role === 'parent') navigate('/parents');
        else if (account.role === 'student') navigate('/students');
        else if (account.role === 'teacher') navigate('/teacher-accounts');
        else navigate('/parents');
    };

    const doDelete = async () => {
        setBusy(true);
        setError('');
        try {
            await axios.delete(`${API_URL}/api/admin/accounts/${account._id}`, {
                ...authHeaders(),
                data: { confirmEmail: confirmEmail.trim() },
            });
            setOpen(false);
            listBackByRole();
        } catch (err) {
            setError(err?.response?.data?.message || 'Delete failed.');
            setBusy(false);
        }
    };

    return (
        <>
            <Card sx={{ p: 3, mb: 3, borderColor: 'error.light', borderStyle: 'solid', borderWidth: 1 }}>
                <Typography variant="h6" sx={{ mb: 1, color: 'error.main' }}>
                    Danger zone
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Permanent deletion removes this account from the database. Only allowed on
                    archived accounts with no remaining references (classes, invoices, lessons,
                    linked children). This cannot be undone.
                </Typography>
                <Button color="error" variant="outlined" onClick={() => setOpen(true)}>
                    Permanently delete…
                </Button>
            </Card>

            <Dialog open={open} onClose={() => !busy && setOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ color: 'error.main' }}>Permanently delete account?</DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        <Alert severity="error" variant="outlined">
                            This action cannot be undone. The account and its login credentials
                            will be removed from the database. Everything that referenced it
                            (classes, invoices, lessons) has already been checked below.
                        </Alert>

                        <Box>
                            <Typography variant="body2" component="div">
                                <strong>{account.firstName} {account.surname}</strong>
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {account.email} · {account.role}
                            </Typography>
                        </Box>

                        {previewErr && <Alert severity="warning">{previewErr}</Alert>}

                        {!preview && !previewErr && (
                            <Typography variant="body2" color="text.secondary">Loading references…</Typography>
                        )}

                        {preview && (
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>References check</Typography>
                                {hasReferences ? (
                                    <>
                                        <Alert severity="warning" sx={{ mb: 1 }}>
                                            This account still has references. Unlink them before deleting.
                                        </Alert>
                                        <Stack spacing={0.5}>
                                            {Object.entries(preview.references)
                                                .filter(([k, v]) => k !== 'total' && v > 0)
                                                .map(([k, v]) => (
                                                    <Typography key={k} variant="body2">
                                                        · {v} {REF_LABELS[k] || k}
                                                    </Typography>
                                                ))}
                                        </Stack>
                                    </>
                                ) : (
                                    <Alert severity="success" variant="outlined">
                                        No references found. Safe to delete.
                                    </Alert>
                                )}
                            </Box>
                        )}

                        {preview && !hasReferences && (
                            <TextField
                                size="small"
                                fullWidth
                                label={`Type "${account.email}" to confirm`}
                                value={confirmEmail}
                                onChange={(e) => setConfirmEmail(e.target.value)}
                                autoComplete="off"
                            />
                        )}

                        {error && <Alert severity="error">{error}</Alert>}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={doDelete} disabled={!canConfirm}>
                        {busy ? 'Deleting…' : 'Delete permanently'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AccountDangerZone;
